import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { Location, PurchaseFacturaRow, PurchasesIvaSummary } from "../../api/types";
import { calendarDayText, money, percentText } from "../../i18n/intl";
import { useAuth } from "../../state/AuthContext";
import { businessToday } from "../../utils/businessDay";
import { paymentStateLabel, paymentStateTone } from "../../utils/invoicePaymentText";
import { RecordInvoicePaymentModal } from "../invoices/RecordInvoicePaymentModal";
import { EmptyState } from "../ui/EmptyState";
import { Select } from "../ui/Select";
import { StatTile } from "../ui/StatTile";
import { MonthNav } from "./MonthNav";
import { PurchasesNotCounted } from "./PurchasesNotCounted";
import { PurchasesToFix } from "./PurchasesToFix";

function paperLink(id: string, label: string) {
  return <Link to={`/app/invoices/${id}`}>{label}</Link>;
}

/**
 * G-121 S4 — "Purchases & IVA paid" (owner only, OD-147).
 *
 * IVA paid in a month = the IVA printed on the month's facturas (by their own
 * date) + the IVA of purchases typed by hand that month (OD-145: a purchase
 * enters the app once, by its paper or by hand). Split by IVA rate as printed,
 * by supplier and by shop (OD-148). Owed = facturas marked not paid yet, from
 * any month (OD-146). Every paper the total could not use is listed under
 * "Not counted" with its reason — never silently dropped.
 *
 * All arithmetic is the server's (`finances/purchases.service.ts`): the card
 * adds nothing, it shows the figures it is sent.
 */
export function PurchasesIvaCard() {
  const { hasRole } = useAuth();
  const isOwner = hasRole("OWNER");

  const today = useMemo(() => businessToday(), []);
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month);
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState<Location[]>([]);
  const [summary, setSummary] = useState<PurchasesIvaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState<PurchaseFacturaRow | null>(null);
  const shopSelectId = useId();

  useEffect(() => {
    if (!isOwner) return;
    void api
      .listLocations()
      .then((list) => setLocations(list.filter((l) => l.isActive)))
      .catch(() => setLocations([]));
  }, [isOwner]);

  const refresh = useCallback(async () => {
    if (!isOwner) return;
    setLoading(true);
    setError(null);
    try {
      setSummary(await api.getPurchasesSummary({ year, month, locationId: locationId || undefined }));
    } catch (err: unknown) {
      setSummary(null);
      setError(err instanceof Error ? err.message : "Could not load purchases");
    } finally {
      setLoading(false);
    }
  }, [isOwner, year, month, locationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!isOwner) return null;

  const s = summary;
  const owedNow = s?.totals.owedEur ?? null;
  const show = (value: string | null) => (loading ? "…" : money(value));

  return (
    <div className="card opcost-card purch-card">
      <div className="opcost-card-head">
        <div>
          <h3 className="opcost-card-title">Purchases &amp; IVA paid</h3>
          <p className="opcost-card-subtitle">
            IVA paid = facturas and credit notes (by their own date) + purchases typed by hand (by day received)
            · albaranes and items-only papers never count
          </p>
        </div>
      </div>

      <div className="opcost-controls">
        <MonthNav
          year={year}
          month={month}
          onChange={(y, m) => {
            setYear(y);
            setMonth(m);
          }}
        />
        <div className="field opcost-shop-field">
          <label htmlFor={shopSelectId}>Shop</label>
          <Select id={shopSelectId} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">All shops</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name} ({loc.code})
              </option>
            ))}
          </Select>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="stat-tile-row">
        <StatTile
          label="IVA paid"
          value={show(s?.ivaPaid.totalEur ?? null)}
          hint={
            s
              ? `${money(s.ivaPaid.fromFacturasEur)} on facturas + ${money(s.ivaPaid.typedNoDeliveryEur)} typed, no delivery${
                  Number(s.ivaPaid.notDeductibleEur) > 0 ? ` + ${money(s.ivaPaid.notDeductibleEur)} with no factura` : ""
                }`
              : undefined
          }
        />
        <StatTile
          label="On facturas"
          value={show(s?.ivaPaid.fromFacturasEur ?? null)}
          hint={
            s
              ? `${s.facturas.count} factura${s.facturas.count === 1 ? "" : "s"}${
                  s.creditNotes.count > 0
                    ? `, ${s.creditNotes.count} credit note${s.creditNotes.count === 1 ? "" : "s"} (${money(s.ivaPaid.creditNotesEur)})`
                    : ""
                }, as printed`
              : undefined
          }
        />
        {/* G-164 option C: receives with no delivery — nothing can link them, so they count as typed. */}
        <StatTile
          label="Typed, no delivery"
          value={show(s?.ivaPaid.typedNoDeliveryEur ?? null)}
          hint={s ? `${s.typed.noDelivery.count} receive${s.typed.noDelivery.count === 1 ? "" : "s"}, IVA at the category's rate` : undefined}
        />
        {/* G-164 option C: typed IVA whose factura may still come — beside the total, never added. */}
        <StatTile
          label="Waiting for a factura"
          value={show(s?.ivaPaid.waitingNotAddedEur ?? null)}
          tone={s && Number(s.ivaPaid.waitingNotAddedEur) > 0 ? "warning" : "neutral"}
          hint={s ? `Not added · ${s.typed.waiting.count} typed purchase${s.typed.waiting.count === 1 ? "" : "s"}` : undefined}
        />
        {/* G-131 (OD-164): typed purchases whose factura will never come. */}
        {s && Number(s.ivaPaid.notDeductibleEur) > 0 ? (
          <StatTile
            label="No factura"
            value={show(s.ivaPaid.notDeductibleEur)}
            tone="warning"
            hint={`Waits closed this month, not deductible: ${s.typed.noFactura.count} purchase${
              s.typed.noFactura.count === 1 ? "" : "s"
            }, ${money(s.typed.noFactura.netEur)} before IVA`}
          />
        ) : null}
        <StatTile
          label="Purchases before IVA"
          value={show(s?.totals.purchasesNetEur ?? null)}
          hint="Facturas + typed by hand"
        />
        <StatTile
          label="Owed now"
          value={show(owedNow)}
          tone={owedNow !== null && Number(owedNow) > 0 ? "warning" : "neutral"}
          hint="Facturas marked not paid yet, any month"
        />
      </div>

      {s && s.notCounted.waitingForFactura.albaranes + s.notCounted.waitingForFactura.deliveries > 0 ? (
        <p className="muted purch-note">
          Waiting for a factura, any month:{" "}
          {[
            s.notCounted.waitingForFactura.albaranes > 0
              ? `${s.notCounted.waitingForFactura.albaranes} ${
                  s.notCounted.waitingForFactura.albaranes === 1 ? "albarán" : "albaranes"
                }`
              : null,
            s.notCounted.waitingForFactura.deliveries > 0
              ? `${s.notCounted.waitingForFactura.deliveries} deliver${
                  s.notCounted.waitingForFactura.deliveries === 1 ? "y" : "ies"
                } typed by hand`
              : null,
          ]
            .filter(Boolean)
            .join(" and ")}
          . Their IVA reaches this card once the factura is linked:{" "}
          <Link to="/app/invoices?view=waiting">Supplier papers</Link>.
        </p>
      ) : null}

      {/* G-164: what is missing — facturas the card cannot count, any month, and the typed IVA waiting on them. */}
      {s ? (
        <PurchasesToFix facturas={s.notCounted.facturasToFix} waitingOnUncounted={s.typed.underUncountedFactura} />
      ) : null}

      {s && s.typed.coveredByFactura.count > 0 ? (
        <p className="muted purch-note">
          {s.typed.coveredByFactura.count} purchase{s.typed.coveredByFactura.count === 1 ? "" : "s"} typed by hand (
          {money(s.typed.coveredByFactura.netEur)} before IVA) are left out: the factura linked to their delivery counts
          them.
        </p>
      ) : null}

      {s && s.facturas.incompleteCount > 0 ? (
        <p className="muted purch-note">
          {s.facturas.incompleteCount} factura{s.facturas.incompleteCount === 1 ? "" : "s"} counted without a
          base or total the reader could not find — added as far as known.
        </p>
      ) : null}

      {s && s.byRate.length === 0 && s.facturas.rows.length === 0 && s.creditNotes.rows.length === 0 && s.typed.count === 0 ? (
        <EmptyState
          title="No purchases this month"
          message="No factura dated this month and nothing typed by hand. Papers that were not counted are listed below."
        />
      ) : null}

      {s && s.byRate.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">IVA by rate</h4>
          {Number(s.facturas.rateTableDifferenceEur) !== 0 ? (
            <p className="muted purch-note">
              The facturas&apos; own per-rate rows differ from their printed IVA totals by{" "}
              {money(s.facturas.rateTableDifferenceEur)}, so the rows below do not add up exactly to
              &ldquo;On facturas&rdquo;.
            </p>
          ) : null}
          <div className="table-scroll" tabIndex={0} role="region" aria-label="IVA by rate">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Rate</th>
                  <th scope="col">Facturas: base</th>
                  <th scope="col">Facturas: IVA</th>
                  <th scope="col">Typed: before IVA</th>
                  <th scope="col">Typed: IVA</th>
                  <th scope="col">IVA paid</th>
                </tr>
              </thead>
              <tbody>
                {s.byRate.map((r) => (
                  <tr key={r.ratePercent ?? "none"}>
                    <th scope="row">{r.ratePercent === null ? "Rate not printed" : `${percentText(r.ratePercent)} %`}</th>
                    <td>{money(r.facturas.baseEur)}</td>
                    <td>{money(r.facturas.ivaEur)}</td>
                    <td>{money(r.typed.netEur)}</td>
                    <td>{money(r.typed.ivaEur)}</td>
                    <td>
                      <strong>{money(r.ivaEur)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {s && s.bySupplier.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">By supplier</h4>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="By supplier">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Supplier</th>
                  <th scope="col">Papers</th>
                  <th scope="col">Before IVA</th>
                  <th scope="col">IVA on facturas</th>
                  <th scope="col">IVA typed by hand</th>
                </tr>
              </thead>
              <tbody>
                {s.bySupplier.map((r) => (
                  <tr key={r.supplierId ?? "none"}>
                    <th scope="row">{r.name}</th>
                    <td>{r.facturas.count}</td>
                    <td>{money(r.netEur)}</td>
                    <td>{money(r.facturas.ivaEur)}</td>
                    <td>{money(r.typed.ivaEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {s && s.byShop.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">By shop</h4>
          <p className="muted purch-note">A factura counts under the shop picked when it was uploaded.</p>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="By shop">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Shop</th>
                  <th scope="col">IVA on facturas</th>
                  <th scope="col">IVA typed by hand</th>
                  <th scope="col">IVA paid</th>
                </tr>
              </thead>
              <tbody>
                {s.byShop.map((r) => (
                  <tr key={r.locationId}>
                    <th scope="row">{r.name}</th>
                    <td>{money(r.facturas.ivaEur)}</td>
                    <td>{money(r.typed.ivaEur)}</td>
                    <td>
                      <strong>{money(r.ivaEur)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {s && s.facturas.rows.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">Facturas this month</h4>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Facturas this month">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Factura</th>
                  <th scope="col">Shop</th>
                  <th scope="col">Total</th>
                  <th scope="col">IVA</th>
                  <th scope="col">Paid</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {s.facturas.rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.invoiceDate ? calendarDayText(r.invoiceDate) : "—"}</td>
                    <td>
                      {paperLink(r.id, `${r.supplierName} ${r.invoiceNumber ?? ""}`.trim())}
                      {r.readerBundlesSeveral ? (
                        <div className="muted">The reader reads it as bundling several albaranes</div>
                      ) : null}
                    </td>
                    <td>{r.locationName}</td>
                    <td>
                      {money(r.grandTotalEur)}
                      {/* G-173: its credit notes take off what is to pay. */}
                      {r.payment.creditedEur !== null && Number(r.payment.creditedEur) !== 0 ? (
                        <div className="muted">
                          {money(r.payment.creditedEur)} credited · {money(r.payment.toPayEur)} to pay
                        </div>
                      ) : null}
                    </td>
                    <td>{money(r.ivaEur)}</td>
                    <td>
                      <span className={`badge ${paymentStateTone(r.payment.state)}`}>
                        {paymentStateLabel(r.payment.state)}
                      </span>
                      {r.payment.owedEur && Number(r.payment.owedEur) > 0 ? (
                        <div className="muted">{money(r.payment.owedEur)} owed</div>
                      ) : null}
                    </td>
                    <td>
                      <button type="button" className="button plain" onClick={() => setPaying(r)}>
                        Record payment
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* G-173: credit notes count by their own date, their figures negative. */}
      {s && s.creditNotes.rows.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">Credit notes this month</h4>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Credit notes this month">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Credit note</th>
                  <th scope="col">Shop</th>
                  <th scope="col">Total</th>
                  <th scope="col">IVA</th>
                  <th scope="col">Corrects</th>
                </tr>
              </thead>
              <tbody>
                {s.creditNotes.rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.invoiceDate ? calendarDayText(r.invoiceDate) : "—"}</td>
                    <td>{paperLink(r.id, `${r.supplierName} ${r.invoiceNumber ?? ""}`.trim())}</td>
                    <td>{r.locationName}</td>
                    <td>{money(r.grandTotalEur)}</td>
                    <td>{money(r.ivaEur)}</td>
                    <td>
                      {r.correctsFactura ? (
                        paperLink(r.correctsFactura.id, r.correctsFactura.label)
                      ) : (
                        <span className="paper-pill paper-pill--warn">Not linked to a factura</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {s && s.typedByCategory.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">Typed by hand, by category</h4>
          {s.typed.missingLines.count > 0 ? (
            <p className="muted purch-note">
              Includes {s.typed.missingLines.count} delivery line{s.typed.missingLines.count === 1 ? "" : "s"} of
              which nothing arrived ({money(s.typed.missingLines.ivaEur)} IVA) — paid when ordered all the same.
            </p>
          ) : null}
          {s.typed.noFactura.count > 0 ? (
            <p className="muted purch-note">
              Includes {s.typed.noFactura.count} purchase{s.typed.noFactura.count === 1 ? "" : "s"} with no factura
              coming ({money(s.typed.noFactura.ivaEur)} IVA) — paid, but not deductible.
            </p>
          ) : null}
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Typed by hand, by category">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col">Purchases</th>
                  <th scope="col">Before IVA</th>
                  <th scope="col">IVA</th>
                  <th scope="col">With IVA</th>
                </tr>
              </thead>
              <tbody>
                {s.typedByCategory.map((r) => (
                  <tr key={r.categoryId}>
                    <th scope="row">{r.label}</th>
                    <td>{r.count}</td>
                    <td>{money(r.netEur)}</td>
                    <td>{money(r.ivaEur)}</td>
                    <td>{money(r.grossEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {s && s.unknownBasis.count > 0 ? (
        <p className="muted purch-note">
          {s.unknownBasis.count} purchase{s.unknownBasis.count === 1 ? "" : "s"} typed before 1 Oct 2026 (
          {money(s.unknownBasis.netEur)}) do not say whether the price included IVA — not added.
        </p>
      ) : null}

      {s && s.owed.length > 0 ? (
        <section className="purch-section">
          <h4 className="purch-section-title">Owed — marked not paid yet</h4>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Owed facturas">
            <table className="table purch-table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Factura</th>
                  <th scope="col">Due</th>
                  <th scope="col">Total</th>
                  <th scope="col">Credited</th>
                  <th scope="col">Paid</th>
                  <th scope="col">Owed</th>
                </tr>
              </thead>
              <tbody>
                {s.owed.map((o) => (
                  <tr key={o.id}>
                    <td>{o.invoiceDate ? calendarDayText(o.invoiceDate) : "—"}</td>
                    <td>{paperLink(o.id, `${o.supplierName} ${o.invoiceNumber ?? ""}`.trim())}</td>
                    <td>{o.dueDate ? calendarDayText(o.dueDate) : "—"}</td>
                    <td>{money(o.grandTotalEur)}</td>
                    <td>{o.creditedEur !== null && Number(o.creditedEur) !== 0 ? money(o.creditedEur) : "—"}</td>
                    <td>{money(o.paidEur)}</td>
                    <td>
                      <strong>{money(o.owedEur)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {s ? <PurchasesNotCounted notCounted={s.notCounted} /> : null}

      {paying ? (
        <RecordInvoicePaymentModal
          invoiceId={paying.id}
          suggestedAmountEur={
            paying.payment.owedEur && Number(paying.payment.owedEur) > 0
              ? paying.payment.owedEur
              : (paying.payment.toPayEur ?? paying.grandTotalEur)
          }
          onSaved={() => {
            setPaying(null);
            void refresh();
          }}
          onClose={() => setPaying(null)}
        />
      ) : null}
    </div>
  );
}
